// Události ISŘ, nápravná opatření a dotazník spokojenosti (F11 11.4; kontrakt tag Isr `/iso/udalosti*`,
// `/iso/napravna-opatreni*`, `/iso/dotaznik*`, veřejně tag Verejne `/verejne/formular/{dotaznik,reklamace}`).
//   • události: seznam s čipy prototypu (otevřené, incidenty a havárie, neshody, reklamace a stížnosti, podněty, vše),
//     rok, souhrn roku; detail s nápravnými opatřeními a historií; Zaevidovat (správce), Nahlásit (každý), přechody
//     Převzít · Uzavřít s důvodem · Znovu otevřít, Odstranit se Zpět; číslo, termín a odpovědného dává server;
//   • nápravná opatření: k události, splnit, vrátit, nový termín, ověřit účinnost;
//   • dotazník: vyhodnocení roku, odpovědi (Odebrat se Zpět, spárovat se zákazníkem), rozesláno, import z Forms,
//     osobní odkaz, odpovědi jednoho zákazníka (karta zákazníka › Spokojenost); veřejná stránka dotazníku
//     a formuláře reklamace bez přihlášení.
// Práva (`muze`) i stav termínu počítá server — UI jen kreslí tlačítka s důvodem.
import type { Schema } from '@ecoser/shared';
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from './klient';
import { nahratFormular } from './nahravani';

export type IsoUdalost = Schema<'IsoUdalost'>;
export type IsoUdalostVstup = Schema<'IsoUdalostVstup'>;
export type IsoUdalostZmena = Schema<'IsoUdalostZmena'>;
export type IsoUdalostHistorie = Schema<'IsoUdalostHistorie'>;
export type NapravneOpatreni = Schema<'NapravneOpatreni'>;
export type NapravneOpatreniVstup = Schema<'NapravneOpatreniVstup'>;
export type DotaznikVyhodnoceni = Schema<'DotaznikVyhodnoceni'>;
export type DotaznikOdpoved = Schema<'DotaznikSpokojenosti'>;
export type DotaznikOtazka = Schema<'DotaznikOtazka'>;
export type DotaznikImportVysledek = Schema<'DotaznikImportVysledek'>;
export type DotaznikOsobniOdkaz = Schema<'DotaznikOsobniOdkaz'>;
export type VerejnyDotaznikStranka = Schema<'VerejnyDotaznikStranka'>;
export type VerejnaReklamaceStranka = Schema<'VerejnaReklamaceStranka'>;

export type TypUdalosti = IsoUdalost['typ'];
export type StavUdalosti = IsoUdalost['stav'];
export type SkupinaUdalosti =
  'otevrene' | 'incidenty' | 'neshody' | 'reklamace' | 'podnety' | 'vse';

export const AKCE_UDALOST_NAHLASIT = 'iso.udalost_nahlasit';
export const AKCE_UDALOST_RESIT = 'iso.udalost_resit';
export const AKCE_ISO_SPRAVOVAT = 'iso.spravovat';

export const KLIC_UDALOSTI = ['iso-udalosti'] as const;
export const KLIC_OPATRENI = ['iso-napravna-opatreni'] as const;
export const KLIC_DOTAZNIK = ['iso-dotaznik'] as const;

/** Druhy pro „Zaevidovat“ (prototyp `isoUdTypy` H:35494 — pořadí a popisky). */
export const TYPY_UDALOSTI: ReadonlyArray<{ typ: TypUdalosti; popis: string }> = [
  { typ: 'incident', popis: 'Incident' },
  { typ: 'havarie', popis: 'Havárie' },
  { typ: 'neshoda', popis: 'Neshoda' },
  { typ: 'reklamace', popis: 'Reklamace' },
  { typ: 'stiznost', popis: 'Stížnost' },
  { typ: 'podnet', popis: 'Podnět / pochvala' },
];

/** Druhy hlášení z provozu (⚑ Nahlásit, prototyp H:444 + podnět — 05/602). */
export const TYPY_NAHLASENI: ReadonlyArray<{ typ: TypUdalosti; popis: string }> = [
  { typ: 'incident', popis: 'Incident (porucha, únik, drobná událost)' },
  { typ: 'havarie', popis: 'Havárie (závažná událost)' },
  { typ: 'neshoda', popis: 'Neshoda (špatný odpad, chybí dokumentace…)' },
  { typ: 'podnet', popis: 'Podnět / pochvala (nápad na zlepšení provozu)' },
];

/** Druhy, u kterých se eviduje kontakt zákazníka (prototyp `jeExterni`). */
export const TYPY_S_KONTAKTEM: readonly TypUdalosti[] = ['reklamace', 'stiznost', 'podnet'];

export interface PoctyUdalosti {
  otevrene: number;
  incidenty: number;
  neshody: number;
  reklamace: number;
  podnety: number;
  vse: number;
  po_terminu: number;
}

export interface SouhrnSkupiny {
  klic: string;
  popis: string;
  celkem: number;
  vyreseno: number;
  prumer_dni: number | null;
}

export interface SeznamUdalosti {
  polozky: IsoUdalost[];
  celkem: number;
  strana: number;
  velikost: number;
  pocty: PoctyUdalosti;
  skupiny: Array<{ klic: SkupinaUdalosti; popis: string }>;
  roky: number[];
  souhrn_rok: number;
  souhrn: SouhrnSkupiny[];
  muze: {
    zaevidovat: { smi: boolean; duvod?: string };
    nahlasit: { smi: boolean; duvod?: string };
  };
}

export interface FiltrUdalosti {
  skupina?: SkupinaUdalosti;
  rok?: number;
  hledat?: string;
  vztah?: 'nahlasil' | 'odpovedny';
  poTerminu?: boolean;
  /** Řazení `pole:asc|desc` (cislo, datum, popis, termin, stav); bez něj nejnovější datum. */
  razeni?: string;
  velikost?: number;
}

/** Čipy prototypu (`isoUdFiltry` H:35757) — popisek + klíč počtu. */
export const CIPY_UDALOSTI: ReadonlyArray<{ klic: SkupinaUdalosti; popis: string }> = [
  { klic: 'otevrene', popis: 'Otevřené' },
  { klic: 'incidenty', popis: 'Incidenty a havárie' },
  { klic: 'neshody', popis: 'Neshody' },
  { klic: 'reklamace', popis: 'Reklamace a stížnosti' },
  { klic: 'podnety', popis: 'Podněty' },
  { klic: 'vse', popis: 'Vše' },
];

export function dotazUdalosti(f: FiltrUdalosti): string {
  const q = new URLSearchParams({ strana: '1', velikost: String(f.velikost ?? 200) });
  if (f.skupina) q.set('skupina', f.skupina);
  if (f.rok) q.set('rok', String(f.rok));
  if (f.hledat?.trim()) q.set('hledat', f.hledat.trim());
  if (f.vztah) q.set('vztah', f.vztah);
  if (f.poTerminu) q.set('po_terminu', 'true');
  if (f.razeni) q.set('razeni', f.razeni);
  return `?${q.toString()}`;
}

export function useUdalosti(f: FiltrUdalosti, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_UDALOSTI, 'seznam', f],
    queryFn: ({ signal }) =>
      api.get<SeznamUdalosti>(`/iso/udalosti${dotazUdalosti(f)}`, { signal }),
    placeholderData: keepPreviousData,
    enabled: povoleno,
  });
}

export function useUdalost(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KLIC_UDALOSTI, 'detail', id],
    queryFn: ({ signal }) =>
      api.get<IsoUdalost>(`/iso/udalosti/${encodeURIComponent(id ?? '')}`, { signal }),
    enabled: Boolean(id),
  });
}

const enc = encodeURIComponent;

export const zalozitUdalost = (telo: IsoUdalostVstup) =>
  api.post<IsoUdalost>('/iso/udalosti', telo);
export const upravitUdalost = (id: string, telo: IsoUdalostZmena) =>
  api.put<IsoUdalost>(`/iso/udalosti/${enc(id)}`, telo);
export const prevzitUdalost = (id: string, telo: { odpovedny_id?: string; termin?: string } = {}) =>
  api.post<IsoUdalost>(`/iso/udalosti/${enc(id)}/resit`, telo);
export const vyresitUdalost = (id: string, telo: { duvod: string; odpoved?: string }) =>
  api.post<IsoUdalost>(`/iso/udalosti/${enc(id)}/vyresit`, telo);
export const znovuOtevritUdalost = (id: string, duvod: string) =>
  api.post<IsoUdalost>(`/iso/udalosti/${enc(id)}/znovu-otevrit`, { duvod });
export const smazatUdalost = (id: string) => api.delete<void>(`/iso/udalosti/${enc(id)}`);
export const obnovitUdalost = (id: string) =>
  api.post<IsoUdalost>(`/iso/udalosti/${enc(id)}/obnovit`);

/**
 * Nápravná opatření jednoho zdroje (např. `iso_audit` — audity systému a externí audity) — ISŘ › Přezkoumání je
 * ukazuje u náprav z externích auditů (05/2221). Server vrátí jen ta, která uživatel vidí.
 */
export function useOpatreniZdroje(zdroj: NapravneOpatreni['zdroj'], povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_OPATRENI, 'zdroj', zdroj],
    queryFn: ({ signal }) =>
      api.get<Schema<'NapravneOpatreniSeznam'>>(
        `/iso/napravna-opatreni?${new URLSearchParams({ zdroj, strana: '1', velikost: '500' }).toString()}`,
        { signal },
      ),
    enabled: povoleno,
  });
}

/**
 * Nápravná opatření z auditů u zákazníků po termínu napříč audity (W37) — Můj den auditora a vedoucího auditora.
 * Server vrátí auditorovi opatření jeho auditů, vedoucímu auditorovi všechna.
 */
export function useOpatreniAudituPoTerminu(povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_OPATRENI, 'audit', 'po-terminu'],
    queryFn: ({ signal }) =>
      api.get<Schema<'NapravneOpatreniSeznam'>>(
        `/iso/napravna-opatreni?${new URLSearchParams({ zdroj: 'audit', po_terminu: 'true', razeni: 'termin', strana: '1', velikost: '100' }).toString()}`,
        { signal },
      ),
    enabled: povoleno,
  });
}

export const zalozitOpatreni = (telo: NapravneOpatreniVstup) =>
  api.post<NapravneOpatreni>('/iso/napravna-opatreni', telo);
/** Splnit opatření; `dukaz` = čím je splnění doložené (u opatření z auditu u zákazníka povinné — W37). */
export const splnitOpatreni = (id: string, dukaz?: string) =>
  api.post<NapravneOpatreni>(
    `/iso/napravna-opatreni/${enc(id)}/hotovo`,
    dukaz ? { dukaz } : undefined,
  );
export const vratitOpatreni = (id: string) =>
  api.post<NapravneOpatreni>(`/iso/napravna-opatreni/${enc(id)}/vratit`);
export const terminOpatreni = (
  id: string,
  telo: { termin?: string; odpovedny_id?: string; duvod?: string },
) => api.post<NapravneOpatreni>(`/iso/napravna-opatreni/${enc(id)}/termin`, telo);
export const overitUcinnost = (
  id: string,
  telo: { ucinne: boolean; poznamka: string; termin?: string },
) => api.post<NapravneOpatreni>(`/iso/napravna-opatreni/${enc(id)}/overit-ucinnost`, telo);
export const smazatOpatreni = (id: string) => api.delete<void>(`/iso/napravna-opatreni/${enc(id)}`);
export const obnovitOpatreni = (id: string) =>
  api.post<NapravneOpatreni>(`/iso/napravna-opatreni/${enc(id)}/obnovit`);

/** Po zápisu: seznam, detail i přehled (signály, Moje hlášení) se načtou znovu. */
export function obnovitUdalosti(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: KLIC_UDALOSTI }),
    qc.invalidateQueries({ queryKey: KLIC_OPATRENI }),
  ]).then(() => undefined);
}

// --- Dotazník spokojenosti ----------------------------------------------------------------------------------------

export interface SeznamOdpovedi {
  polozky: DotaznikOdpoved[];
  celkem: number;
  strana: number;
  velikost: number;
}

export function useVyhodnoceniDotazniku(rok: number | undefined, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_DOTAZNIK, 'vyhodnoceni', rok ?? 'letos'],
    queryFn: ({ signal }) =>
      api.get<DotaznikVyhodnoceni>(`/iso/dotaznik/vyhodnoceni${rok ? `?rok=${rok}` : ''}`, {
        signal,
      }),
    placeholderData: keepPreviousData,
    enabled: povoleno,
  });
}

export function useOdpovediDotazniku(rok: number | undefined, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_DOTAZNIK, 'odpovedi', rok ?? 'vse'],
    queryFn: ({ signal }) =>
      api.get<SeznamOdpovedi>(
        `/iso/dotaznik/odpovedi?strana=1&velikost=500${rok ? `&rok=${rok}` : ''}`,
        { signal },
      ),
    placeholderData: keepPreviousData,
    enabled: povoleno,
  });
}

/** Odpovědi jednoho zákazníka, nejnovější první (karta zákazníka › Spokojenost, doplněk 05/605 z 27. 9. 2026). */
export function useOdpovediZakaznika(zakaznikId: string, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_DOTAZNIK, 'odpovedi', 'zakaznik', zakaznikId],
    queryFn: ({ signal }) =>
      api.get<SeznamOdpovedi>(
        `/iso/dotaznik/odpovedi?strana=1&velikost=100&zakaznik_id=${enc(zakaznikId)}`,
        { signal },
      ),
    enabled: povoleno,
  });
}

/** Aktivní hodnocené oblasti v pořadí formuláře — záhlaví tabulky odpovědí mimo vyhodnocení roku. */
export function useOtazkyDotazniku(povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_DOTAZNIK, 'otazky'],
    queryFn: ({ signal }) => api.get<DotaznikOtazka[]>('/iso/dotaznik/otazky', { signal }),
    staleTime: 5 * 60_000,
    enabled: povoleno,
  });
}

export const zapsatRozeslani = (rok: number, rozeslano: number) =>
  api.post<Schema<'DotaznikRozeslani'>>('/iso/dotaznik/rozeslani', { rok, rozeslano });
export const odebratOdpoved = (id: string) => api.delete<void>(`/iso/dotaznik/odpovedi/${enc(id)}`);
export const vratitOdpoved = (id: string) =>
  api.post<DotaznikOdpoved>(`/iso/dotaznik/odpovedi/${enc(id)}/obnovit`);
export const sparovatOdpoved = (id: string, zakaznikId: string | null) =>
  api.put<DotaznikOdpoved>(`/iso/dotaznik/odpovedi/${enc(id)}`, { zakaznik_id: zakaznikId });
export const osobniOdkazDotazniku = (zakaznikId: string) =>
  api.post<DotaznikOsobniOdkaz>('/iso/dotaznik/odkazy', { zakaznik_id: zakaznikId });

export function importovatForms(soubor: File): Promise<DotaznikImportVysledek> {
  const data = new FormData();
  data.append('soubor', soubor, soubor.name);
  return nahratFormular<DotaznikImportVysledek>('/iso/dotaznik/import', data);
}

// --- Veřejné formuláře (bez přihlášení) ----------------------------------------------------------------------------

export function useVerejnyDotaznik(token: string | undefined) {
  return useQuery({
    queryKey: ['verejny-dotaznik', token ?? 'spolecny'],
    queryFn: ({ signal }) =>
      api.get<VerejnyDotaznikStranka>(
        `/verejne/formular/dotaznik${token ? `/${enc(token)}` : ''}`,
        { signal, bezHlaseniSite: true },
      ),
    staleTime: Infinity,
    retry: false,
  });
}

export interface VerejnyDotaznikVstup {
  token?: string;
  hodnoceni: Record<string, number>;
  firma?: string;
  email?: string;
  vzkaz?: string;
  web?: string;
}

export const odeslatDotaznik = (telo: VerejnyDotaznikVstup) =>
  api.post<{ prijato: boolean }>('/verejne/formular/dotaznik', telo, { bezHlaseniSite: true });

export function useVerejnaReklamace() {
  return useQuery({
    queryKey: ['verejna-reklamace'],
    queryFn: ({ signal }) =>
      api.get<VerejnaReklamaceStranka>('/verejne/formular/reklamace', {
        signal,
        bezHlaseniSite: true,
      }),
    staleTime: Infinity,
    retry: false,
  });
}

export interface VerejnaReklamaceVstup {
  typ: 'reklamace' | 'stiznost' | 'podnet';
  /** Jméno a příjmení. */
  jmeno?: string;
  firma?: string;
  /** E‑mail nebo telefon — aspoň jedno (F1 a). */
  email?: string;
  telefon?: string;
  text: string;
  /** Kdy se to stalo (RRRR-MM-DD). */
  kdy?: string;
  /** Kde se to stalo. */
  kde?: string;
  zakazka_cislo?: string;
  web?: string;
}

export interface VerejnaReklamacePrijata {
  prijato: boolean;
  cislo?: string;
  typ?: string;
  typ_popis?: string;
  termin?: string;
  potvrzeni_email?: boolean;
  pocet_priloh?: number;
}

/** Podání bez příloh jde jako JSON, s přílohami jako formulář (multipart, pole `prilohy`). */
export const odeslatReklamaci = (telo: VerejnaReklamaceVstup, prilohy: readonly File[] = []) => {
  if (prilohy.length === 0) {
    return api.post<VerejnaReklamacePrijata>('/verejne/formular/reklamace', telo, {
      bezHlaseniSite: true,
    });
  }
  const data = new FormData();
  for (const [klic, hodnota] of Object.entries(telo)) {
    if (typeof hodnota === 'string') data.append(klic, hodnota);
  }
  for (const soubor of prilohy) data.append('prilohy', soubor, soubor.name);
  return api.post<VerejnaReklamacePrijata>('/verejne/formular/reklamace', data, {
    bezHlaseniSite: true,
  });
};

/** Adresa veřejného formuláře reklamace pro web firmy (prototyp `isoReklamaceUrl`). */
export function adresaFormulareReklamace(): string {
  return `${window.location.origin}/formular/reklamace`;
}
